import { Slider as SliderPrimitive } from 'radix-ui';
import { Label } from '../label';
import { InfoTooltip } from './InfoTooltip';
import React, { type ReactNode } from 'react';
import { cn } from '../../cn';

type SliderScoreProps = {
  value?: number | null;
  label?: string;
  tooltipContent?: ReactNode;
  rangeClassName?: string;
  thumbClassName?: string;
  trackClassName?: string;
};

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    trackClassName?: string;
    rangeClassName?: string;
    thumbClassName?: string;
  }
>(
  (
    { className, trackClassName, rangeClassName, thumbClassName, ...props },
    ref,
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20',
          trackClassName,
        )}
      >
        <SliderPrimitive.Range
          className={cn('absolute h-full bg-primary', rangeClassName)}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className={cn(
          'block h-4 w-4 rounded-full border border-primary/50 bg-background shadow-sm transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          thumbClassName,
        )}
      />
    </SliderPrimitive.Root>
  ),
);
Slider.displayName = SliderPrimitive.Root.displayName;

const MIN_HIGH = 90;
const MIN_MEDIUM = 70;

export function SliderScoreLabel({
  value,
  label,
  tooltipContent,
}: SliderScoreProps) {
  const getClassNameByValue = () => {
    if (typeof value !== 'number') {
      return {
        rangeClassName: 'bg-green-500',
        trackClassName: 'bg-gray-500/20',
      };
    }

    if (value >= MIN_HIGH) {
      return {
        rangeClassName: 'bg-green-600',
        trackClassName: 'bg-green-600/20',
      };
    }

    if (value >= MIN_MEDIUM) {
      return {
        rangeClassName: 'bg-green-600',
        trackClassName: 'bg-green-600/20',
      };
    }

    return {
      rangeClassName: 'bg-red-500',
      trackClassName: 'bg-red-600/20',
    };
  };

  const { rangeClassName, trackClassName } = getClassNameByValue();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Label>{label}</Label>
          {tooltipContent ? (
            <InfoTooltip className="max-w-[400px]" content={tooltipContent} />
          ) : null}
        </div>
        {typeof value === 'number' && (
          <span className="text-base font-semibold">{value}%</span>
        )}
      </div>

      <Slider
        min={0}
        max={100}
        id="brightness"
        value={[value ?? 0]}
        rangeClassName={cn('bg-green-600', rangeClassName)}
        thumbClassName={cn('hidden')}
        trackClassName={cn('bg-green-600/20', trackClassName)}
      />
    </div>
  );
}
